import { EnvironmentProviders, Provider } from '@angular/core';
import { provideLivreurLock } from './livreur-lock';
import { APP_MENU_DEFINITION, APP_MENU_SECTIONS, AppMenuLeafDef, AppMenuSectionDef } from '@app/core/constants/app-menu.definition';

/**
 * Définition du menu côté `custom` : base MNC + adaptations HealthEat.
 */
function cloneMenu(sections: readonly AppMenuSectionDef[]): AppMenuSectionDef[] {
  return sections.map((s) => ({
    ...s,
    items: s.items.map((i) => ({ ...i })),
  }));
}

function removeItemsByRouterLink(sections: AppMenuSectionDef[], routerLinks: readonly string[]): AppMenuSectionDef[] {
  const set = new Set(routerLinks);
  return sections.map((s) => ({
    ...s,
    items: s.items.filter((i) => !(i.routerLink && set.has(i.routerLink))),
  }));
}

function findSectionIndex(sections: AppMenuSectionDef[], by: { label: string }): number {
  return sections.findIndex((s) => s.label === by.label);
}

function findItemIndex(items: readonly AppMenuLeafDef[], by: { routerLink?: string; label?: string }): number {
  if (by.routerLink) {
    const idx = items.findIndex((i) => i.routerLink === by.routerLink);
    if (idx !== -1) return idx;
  }
  if (by.label) {
    return items.findIndex((i) => i.label === by.label);
  }
  return -1;
}

function insertItemIntoSection(
  sections: AppMenuSectionDef[],
  section: { label: string },
  item: AppMenuLeafDef,
  where: { index?: number; before?: { routerLink?: string; label?: string }; after?: { routerLink?: string; label?: string } } = {},
): AppMenuSectionDef[] {
  const sIdx = findSectionIndex(sections, section);
  if (sIdx === -1) return sections;
  const s = sections[sIdx];
  const nextItems = [...s.items];

  let insertAt = where.index ?? -1;
  if (insertAt < 0 && where.before) {
    const idx = findItemIndex(nextItems, where.before);
    if (idx !== -1) insertAt = idx;
  }
  if (insertAt < 0 && where.after) {
    const idx = findItemIndex(nextItems, where.after);
    if (idx !== -1) insertAt = idx + 1;
  }
  if (insertAt < 0) insertAt = nextItems.length;

  nextItems.splice(insertAt, 0, item);
  const nextSections = [...sections];
  nextSections[sIdx] = { ...s, items: nextItems };
  return nextSections;
}

/** Insère une section entière juste avant une autre (à la fin si elle est absente). */
function insertSectionBefore(
  sections: AppMenuSectionDef[],
  before: { label: string },
  section: AppMenuSectionDef,
): AppMenuSectionDef[] {
  const idx = findSectionIndex(sections, before);
  const next = [...sections];
  next.splice(idx === -1 ? next.length : idx, 0, section);
  return next;
}

/**
 * Neutralise le filtrage du menu par permission Spatie.
 *
 * ⚠️ La raison N'EST PLUS l'API : depuis l'alignement, `adminLogin` renvoie bien
 * `roles.permissions`, `permissions` et `all_permissions` (vérifié le 2026-07-13 contre
 * l'API locale : le compte `shop-admin` reçoit ses 20 permissions), et `auth/ping` renvoie
 * `permission_names`.
 *
 * Le blocage est désormais côté DONNÉES du caser : son catalogue Spatie est resté à
 * 36 permissions (héritage pré-alignement) et n'en contient aucune de celles que le menu
 * MNC exige : `view clients`, `view companies`, `view quotes`, `view invoices`,
 * `view settings`, `view subscriptions`, `view payment methods`. Le rôle `shop-admin`
 * (compte administrateur de la boutique) n'en détient que 20 sur les 36.
 *
 * Conséquence mesurée si l'on filtrait : sur les 18 entrées de menu portant une permission,
 * 3 seulement resteraient visibles pour `shop-admin` (Catalogue, Coupons, Commandes) et
 * 15 disparaîtraient (Contacts, Entreprises, Achats, Catégories, Devis, Factures,
 * Abonnements, Taxes, Devises, Langues, Livraisons, Moyens de paiement, Utilisateurs,
 * Paramètres…). Régression fonctionnelle inacceptable → on garde le strip.
 *
 * Pour lever ce contournement (dans cet ordre) :
 *   1. `php artisan db:seed --class=Database\\Seeders\\Moni\\UserTableSeeder` sur l'API du
 *      caser : il crée le catalogue complet de permissions (clients, devis, factures,
 *      paramètres…) — attention, il synchronise aussi `super-admin` et crée le rôle
 *      `Administrateur` ;
 *   2. ré-attribuer les permissions manquantes au rôle historique `shop-admin` (BO :
 *      Administration → Rôles), le seeder ne le touche pas ;
 *   3. supprimer l'appel à `stripPermissions()` ci-dessous et vérifier le menu avec un
 *      compte non `super-admin`.
 *
 * Les gardes `superAdminOnly` / `roles` restent actifs (ils ne dépendent que des rôles).
 */
function stripPermissions(sections: AppMenuSectionDef[]): AppMenuSectionDef[] {
  return sections.map((s) => ({
    ...s,
    items: s.items.map((i) => {
      const { permission: _p, permissionMode: _m, ...rest } = i;
      return rest as AppMenuLeafDef;
    }),
  }));
}

function buildCustomMenu(): readonly AppMenuSectionDef[] {
  let sections = cloneMenu(APP_MENU_SECTIONS);

  sections = stripPermissions(sections);

  // Entrées dont la route n'existe pas encore dans ce projet, et entrées retirées du
  // menu HealthEat à la demande de l'exploitant (2026-09-22) : Caisse (POS). Planning
  // (SKEDI), Site vitrine (EEZILI) et Récapitulatif annuel ne sont plus ajoutés.
  sections = removeItemsByRouterLink(sections, [
    '/moni/livraisons',
    '/pos',
  ]);

  sections = insertItemIntoSection(
    sections,
    { label: 'Catalogue' },
    {
      label: 'Stock',
      icon: 'text-xl fa-light fa-database',
      routerLink: '/moni/stock',
      roleEditorSkip: true,
    },
    { after: { routerLink: '/moni/categories' } },
  );

  // Les écrans propres à HealthEat tiennent dans leur propre section, placée en
  // TÊTE de menu (avant « Contacts ») : c'est le quotidien de l'exploitant. Dispersés
  // dans « Ventes », ils se confondaient avec les écrans du socle et devaient porter
  // « HealthEat » dans leur libellé pour qu'on les distingue.
  sections = insertSectionBefore(sections, { label: 'Contacts' }, {
    label: 'HealthEat',
    icon: 'text-xl fa-light fa-bowl-food',
    items: [
      {
        label: 'Planning',
        icon: 'text-xl fa-light fa-calendar-days',
        routerLink: '/custom/planning',
        roleEditorSkip: true,
      },
      {
        label: 'Suivi des livraisons',
        icon: 'text-xl fa-light fa-truck',
        routerLink: '/custom/suivi',
        roleEditorSkip: true,
      },
      {
        label: 'Créneaux des plats',
        icon: 'text-xl fa-light fa-clock',
        routerLink: '/custom/creneaux',
        roleEditorSkip: true,
      },
      {
        label: 'Packages',
        icon: 'text-xl fa-light fa-box-open',
        routerLink: '/custom/packages',
        roleEditorSkip: true,
      },
      {
        label: 'Paramètres',
        icon: 'text-xl fa-light fa-sliders',
        routerLink: '/custom/livraison',
        roleEditorSkip: true,
      },
    ],
  });

  return sections;
}

/**
 * Seul point d'entrée du caser dans `app.config.ts` (fichier partagé avec MNC-APP) :
 * il porte aussi le verrou livreur, qui n'a pas d'autre accroche sans toucher au socle.
 */
export function provideCustomAppMenuDefinition(): (Provider | EnvironmentProviders)[] {
  return [
    { provide: APP_MENU_DEFINITION, useFactory: () => buildCustomMenu() },
    provideLivreurLock(),
  ];
}
